import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses, formatAmount } from "../theme";
import {
  apiErrorMessage,
  fetchPaidPeriods,
  fetchPaymentProofUrl,
  fetchPaymentReceipt,
} from "../services/payrollService";
import { fetchSquads, fetchTeamMembers } from "../../team/services/teamService";
import type { PaidPeriodGroup, PaidPeriodPayment, PaidPeriodsResponse } from "../types";

// ---------------------------------------------------------------------------
// Registro de periodos pagados (#1141): cada quincena (o mes) con a quién se
// le pagó, cuánto, su comprobante y cuánto costó el periodo — por moneda y en
// pesos con el tipo de cambio del día de pago (o el capturado en el pago).
//
// Filtros: agrupar por quincena o mes, rango de meses y selección múltiple de
// colaboradores y/o squads (se unen: un squad aporta a su líder y miembros).
// Quien no administra recibe del servidor solo lo suyo.
// ---------------------------------------------------------------------------

type GroupBy = "half" | "month";
interface Option {
  id: string;
  label: string;
}

const thisMonth = () => new Date().toISOString().slice(0, 7);
function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function openUrl(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Lista desplegable con casillas: elegir uno o más. */
function MultiSelect({
  label,
  options,
  value,
  onChange,
  testId,
  tc,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  testId: string;
  tc: ReturnType<typeof buildThemeClasses>;
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const shown = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="relative" ref={ref}>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-48 items-center justify-between rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.bg} ${tc.text}`}
      >
        <span className="truncate">
          {value.length === 0
            ? t("payroll.paidPeriods.all")
            : value.length === 1
              ? options.find((o) => o.id === value[0])?.label ?? "1"
              : t("payroll.paidPeriods.selected", { count: value.length })}
        </span>
        <i className="ri-arrow-down-s-line ml-2" />
      </button>
      {open && (
        <div className={`absolute z-20 mt-1 w-64 rounded-lg border p-2 shadow-lg ${tc.border} ${tc.bg}`}>
          <input
            className={`mb-2 w-full rounded border px-2 py-1 text-xs ${tc.border} ${tc.bg} ${tc.text}`}
            placeholder={t("payroll.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="max-h-56 overflow-y-auto">
            {shown.map((o) => (
              <label key={o.id} className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm ${tc.text} ${tc.buttonHover}`}>
                <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            {shown.length === 0 && <p className={`px-1 py-2 text-xs ${tc.textSecondary}`}>—</p>}
          </div>
          {value.length > 0 && (
            <button type="button" className="mt-2 text-xs text-primary-500" onClick={() => onChange([])}>
              {t("payroll.paidPeriods.clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PaidPeriodsRegistry() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [groupBy, setGroupBy] = useState<GroupBy>("half");
  const [to, setTo] = useState(thisMonth());
  const [from, setFrom] = useState(shiftMonth(thisMonth(), -5));
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [squadIds, setSquadIds] = useState<string[]>([]);
  const [data, setData] = useState<PaidPeriodsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [members, setMembers] = useState<Option[]>([]);
  const [squads, setSquads] = useState<Option[]>([]);

  const isAdmin = data?.isStaffAdmin === true;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchPaidPeriods({ groupBy, from, to, teamMemberIds: memberIds, squadIds }));
    } catch (err) {
      setError(apiErrorMessage(err) || t("payroll.paidPeriods.loadError"));
    } finally {
      setLoading(false);
    }
  }, [groupBy, from, to, memberIds, squadIds, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Los catálogos de personas y squads solo hacen falta a quien ve a todos.
  useEffect(() => {
    if (!isAdmin) return;
    fetchTeamMembers({ limit: 200 })
      .then(({ items }) =>
        setMembers(
          items
            .map((m) => ({ id: m.id, label: [m.name, m.lastName].filter(Boolean).join(" ") || m.id }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ),
      )
      .catch(() => setMembers([]));
    fetchSquads({ limit: 200 })
      .then(({ items }) =>
        setSquads(items.map((s) => ({ id: s.id, label: s.name || s.id })).sort((a, b) => a.label.localeCompare(b.label))),
      )
      .catch(() => setSquads([]));
  }, [isAdmin]);

  const downloadProof = async (p: PaidPeriodPayment) => {
    try {
      openUrl((await fetchPaymentProofUrl(p.id)).url);
    } catch (err) {
      setError(apiErrorMessage(err) || t("payroll.payments.downloadError"));
    }
  };

  const downloadReceipt = async (p: PaidPeriodPayment) => {
    try {
      saveBlob(await fetchPaymentReceipt(p.id), `recibo-${p.period}.pdf`);
    } catch (err) {
      setError(apiErrorMessage(err) || t("payroll.payments.downloadError"));
    }
  };

  const totalsText = (totals: Record<string, number>) =>
    Object.entries(totals)
      .map(([cur, amt]) => formatAmount(amt, cur))
      .join(" · ") || "—";

  const mxnText = (g: { totalMxn: number; missingRate: number }) =>
    `${formatAmount(g.totalMxn, "MXN")}${g.missingRate ? ` ${t("payroll.paidPeriods.incomplete", { count: g.missingRate })}` : ""}`;

  const rateNote = (p: PaidPeriodPayment) => {
    if (p.rateSource === "base" || p.rate === null) return null;
    return t(p.rateSource === "pago" ? "payroll.paidPeriods.ratePayment" : "payroll.paidPeriods.rateDay", { rate: p.rate });
  };

  const summary = data?.summary;
  const groups = data?.groups ?? [];
  const input = `rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.bg} ${tc.text}`;
  const label = `mb-1 block text-xs font-medium ${tc.textSecondary}`;
  const ghost = `inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs ${tc.border} ${tc.text} ${tc.buttonHover}`;

  const kpis = useMemo(
    () =>
      summary
        ? [
            { k: "cost", label: t("payroll.paidPeriods.totalCost"), value: mxnText(summary), strong: true },
            { k: "cur", label: t("payroll.paidPeriods.byCurrency"), value: totalsText(summary.totals) },
            { k: "count", label: t("payroll.paidPeriods.payments"), value: `${summary.count} · ${t("payroll.paidPeriods.periodsCount", { count: summary.periods })}` },
            {
              k: "proof",
              label: t("payroll.paidPeriods.withoutProof"),
              value: String(summary.withoutProof),
              danger: summary.withoutProof > 0,
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, t],
  );

  return (
    <div data-testid="paid-periods-registry">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className={label}>{t("payroll.paidPeriods.groupBy")}</label>
          <div className={`inline-flex overflow-hidden rounded-lg border ${tc.border}`}>
            {(["half", "month"] as GroupBy[]).map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`group-${g}`}
                onClick={() => setGroupBy(g)}
                className={`px-3 py-2 text-sm ${groupBy === g ? "bg-primary-500 text-white" : `${tc.bg} ${tc.text}`}`}
              >
                {t(g === "half" ? "payroll.paidPeriods.byHalf" : "payroll.paidPeriods.byMonth")}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={label}>{t("payroll.paidPeriods.from")}</label>
          <input type="month" data-testid="range-from" className={input} value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={label}>{t("payroll.paidPeriods.to")}</label>
          <input type="month" data-testid="range-to" className={input} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
        {isAdmin && (
          <>
            <MultiSelect
              label={t("payroll.paidPeriods.members")}
              options={members}
              value={memberIds}
              onChange={setMemberIds}
              testId="filter-members"
              tc={tc}
            />
            <MultiSelect label={t("payroll.paidPeriods.squads")} options={squads} value={squadIds} onChange={setSquadIds} testId="filter-squads" tc={tc} />
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
          <i className="ri-error-warning-line mr-1" />
          {error}
        </div>
      )}

      {summary && summary.count > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpis.map((k) => (
            <div key={k.k} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <p className={`text-xs ${tc.textSecondary}`}>{k.label}</p>
              <p className={`text-base font-semibold ${k.danger ? "text-red-500" : k.strong ? "text-emerald-500" : tc.text}`}>{k.value}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          <i className="ri-loader-4-line mr-1 animate-spin" />
          {t("payroll.loading")}
        </div>
      ) : groups.length === 0 ? (
        <div className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          {t("payroll.paidPeriods.empty")}
        </div>
      ) : (
        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-xs" data-testid="paid-periods-table">
              <thead className="bg-primary-500 text-white">
                <tr>
                  <th className="p-3 text-left font-bold">{t("payroll.paidPeriods.period")}</th>
                  <th className="p-3 text-center font-bold">{t("payroll.paidPeriods.people")}</th>
                  <th className="p-3 text-right font-bold">{t("payroll.paidPeriods.byCurrency")}</th>
                  <th className="p-3 text-right font-bold">{t("payroll.paidPeriods.costMxn")}</th>
                  <th className="p-3 text-center font-bold">{t("payroll.paidPeriods.withoutProof")}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g: PaidPeriodGroup) => (
                  <Fragment key={g.key}>
                    <tr
                      className={`cursor-pointer ${isDarkMode ? "bg-[#232323] text-white" : "bg-gray-50 text-gray-900"}`}
                      onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}
                      data-testid={`period-${g.key}`}
                    >
                      <td className="p-2 font-semibold">
                        <i className={`mr-1 ${open[g.key] ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}`} />
                        {g.label}
                        <span className={`ml-2 font-normal ${tc.textSecondary}`}>
                          {g.start} → {g.end}
                        </span>
                      </td>
                      <td className="p-2 text-center">{g.members.length}</td>
                      <td className="p-2 text-right whitespace-nowrap">{totalsText(g.totals)}</td>
                      <td className="p-2 text-right font-semibold whitespace-nowrap">{mxnText(g)}</td>
                      <td className="p-2 text-center">
                        {g.withoutProof > 0 ? (
                          <span className="rounded bg-red-600 px-2 py-0.5 text-[10px] text-white">{g.withoutProof}</span>
                        ) : (
                          <i className="ri-check-line text-emerald-500" />
                        )}
                      </td>
                    </tr>
                    {open[g.key] &&
                      g.members.map((m) =>
                        m.payments.map((p, i) => (
                          <tr key={`${g.key}-${p.id}`} className={isDarkMode ? "bg-[#1C1C1C] text-white" : "bg-white text-gray-900"}>
                            <td className="p-2 pl-8">
                              {i === 0 ? <span className="font-medium">{m.name}</span> : null}
                              <span className={`block text-[10px] ${tc.textSecondary}`}>
                                {p.paidAt} · {t(`payroll.payments.methods.${p.method}`)}
                                {p.reference ? ` · ${p.reference}` : ""}
                                {g.key !== p.period ? ` · ${p.period}` : ""}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <button type="button" className={ghost} onClick={() => downloadReceipt(p)}>
                                <i className="ri-file-pdf-2-line" />
                                PDF
                              </button>
                            </td>
                            <td className="p-2 text-right whitespace-nowrap">{formatAmount(p.amount, p.currency)}</td>
                            <td className="p-2 text-right whitespace-nowrap">
                              {p.amountMxn === null ? "—" : formatAmount(p.amountMxn, "MXN")}
                              {rateNote(p) && <span className={`block text-[10px] ${tc.textSecondary}`}>{rateNote(p)}</span>}
                            </td>
                            <td className="p-2 text-center">
                              {p.proof ? (
                                <button type="button" className={ghost} onClick={() => downloadProof(p)} title={p.proof.name}>
                                  <i className="ri-file-download-line" />
                                  {t("payroll.payments.download")}
                                </button>
                              ) : (
                                <span className="rounded bg-red-600 px-2 py-0.5 text-[10px] text-white">{t("payroll.payments.noProof")}</span>
                              )}
                            </td>
                          </tr>
                        )),
                      )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
